/**
 * Kernel stems and learner answers as LaTeX (#362), so the web renders maths with KaTeX: the item's stem, and the live preview
 * of what the learner is typing. Pure functions of their input; the output is a string of TeX source and nothing else (never
 * markup), and a learner's text only ever reaches it through the kernel's parser, so an identifier is the only free text in it
 * and is escaped.
 *
 * The preview is a convenience and decides nothing: `previewTexOfAnswer` returns `null` for text it cannot read, and the
 * grader (`grade.ts`) is the only thing that says whether an answer is in a form DaVinci can check.
 *
 * Pure: imports only from this package.
 */

import { ParseError, parseExpr } from '../certificates/index.js';
import type { Relation } from '../certificates/index.js';
import { stemExprOfKernel, type PrepStem, type StemConstraint, type StemDomain, type StemExpr } from './stem.js';

const ATOM = 6;

function precedence(e: StemExpr): number {
  switch (e.k) {
    case 'add':
    case 'sub':
      return 1;
    case 'mul':
      return 2;
    case 'neg':
      return 3;
    case 'div':
      return 4;
    case 'pow':
      return 5;
    default:
      return ATOM;
  }
}

function texName(name: string): string {
  const safe = name.replace(/_/gu, '\\_');
  return Array.from(name).length === 1 && /^\p{L}$/u.test(name) ? safe : `\\mathit{${safe}}`;
}

const paren = (inner: string): string => `\\left(${inner}\\right)`;

/** `inner`, parenthesised when its precedence is below `minimum`. */
function wrap(e: StemExpr, minimum: number): string {
  const text = texOfExpr(e);
  return precedence(e) < minimum ? paren(text) : text;
}

export function texOfExpr(e: StemExpr): string {
  switch (e.k) {
    case 'int':
      return e.v;
    case 'var':
      return texName(e.name);
    case 'neg':
      return `-${e.a.k === 'neg' || precedence(e.a) < 2 ? paren(texOfExpr(e.a)) : texOfExpr(e.a)}`;
    case 'add':
      return e.b.k === 'neg' ? `${wrap(e.a, 1)} - ${wrap(e.b.a, 2)}` : `${wrap(e.a, 1)} + ${wrap(e.b, 2)}`;
    case 'sub':
      return `${wrap(e.a, 1)} - ${e.b.k === 'neg' ? paren(texOfExpr(e.b)) : wrap(e.b, 2)}`;
    case 'mul': {
      // `2x` and `3(x+1)`, as a person writes them; anything else keeps the dot so `2 \cdot 3` is not read as 23.
      const implicit = e.a.k === 'int' && (e.b.k === 'var' || (e.b.k === 'pow' && e.b.base.k === 'var') || e.b.k === 'add' || e.b.k === 'sub');
      if (implicit) return `${texOfExpr(e.a)}${wrap(e.b, 2)}`;
      return `${wrap(e.a, 2)} \\cdot ${wrap(e.b, 4)}`;
    }
    case 'div':
      return `\\frac{${texOfExpr(e.a)}}{${texOfExpr(e.b)}}`;
    case 'pow':
      return `${wrap(e.base, ATOM)}^{${e.exp}}`;
  }
}

const RELATION_TEX: Readonly<Record<Relation, string>> = { '=': '=', '≠': '\\neq', '<': '<', '≤': '\\leq', '>': '>', '≥': '\\geq' };

export const texOfConstraint = (c: StemConstraint): string => `${texOfExpr(c.lhs)} ${RELATION_TEX[c.rel]} ${texOfExpr(c.rhs)}`;

const DOMAIN_TEX: Readonly<Record<StemDomain, string>> = { N0: '\\mathbb{N}_0', N1: '\\mathbb{N}', Z: '\\mathbb{Z}', Q: '\\mathbb{Q}' };

export interface PrepStemTex {
  /** The quantifier and domain: `\forall x, y \in \mathbb{Z}` or `\exists x \in \mathbb{N}`. */
  readonly quantifier: string;
  /** One TeX string per `given` constraint. */
  readonly given: readonly string[];
  /** One TeX string per `claim` constraint. */
  readonly claim: readonly string[];
  /** The whole statement on one line, for a renderer that shows one formula. */
  readonly statement: string;
}

/** The stem as TeX. Hypotheses read `H1 \wedge H2 \Rightarrow C`; several constraints are joined by `\wedge`. */
export function texOfStem(stem: PrepStem): PrepStemTex {
  const names = stem.variables.map(texName).join(', ');
  const quantifier = `${stem.taskKind === 'counterexample' ? '\\forall' : '\\exists'} ${names} \\in ${DOMAIN_TEX[stem.domain]}`;
  const given = stem.given.map(texOfConstraint);
  const claim = stem.claim.map(texOfConstraint);
  const conjunction = (parts: readonly string[]): string => parts.join(' \\wedge ');
  const body = given.length === 0 ? conjunction(claim) : `${conjunction(given)} \\Rightarrow ${conjunction(claim)}`;
  return { quantifier, given, claim, statement: `${quantifier}:\\; ${body}` };
}

/**
 * The live preview of an answer typed as `x = 3, y = -1/2`: each piece `name = expression` rendered, joined by `,\;`. `null` when
 * any piece does not read (the web then shows the raw text, LTR, and the grader will say `unparseable` on submit). Splits on a
 * comma only at bracket depth zero, so `(1, 2)` is not cut.
 */
export function previewTexOfAnswer(text: string): string | null {
  const pieces: string[] = [];
  let depth = 0;
  let current = '';
  for (const ch of text) {
    if (ch === '(' || ch === '{') depth += 1;
    if (ch === ')' || ch === '}') depth -= 1;
    if (ch === ',' && depth === 0) {
      pieces.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  pieces.push(current);
  const rendered: string[] = [];
  for (const piece of pieces) {
    const sides = piece.split('=');
    if (sides.length !== 2) return null;
    const name = (sides[0] as string).trim();
    if (!/^[\p{L}_][\p{L}0-9_]*$/u.test(name)) return null;
    try {
      const value = stemExprOfKernel(parseExpr((sides[1] as string).trim()));
      if (value === undefined) return null;
      rendered.push(`${texName(name)} = ${texOfExpr(value)}`);
    } catch (error) {
      if (error instanceof ParseError) return null;
      throw error;
    }
  }
  return rendered.join(',\\;');
}
